import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Filter,
  Edit,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Mail,
  Phone,
  Building,
  Calendar,
  Save,
  X,
  Shield,
  Percent,
  AlertCircle as AlertCircleIcon
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  callAuthPostWithFallback,
  STAFF_MANAGEMENT_PRIMARY_URL,
  AUTH_LAMBDA_FUNCTION_URL,
  callStaffLambdaDirect
} from '../../utils/authFallback';

const initialEditFormState = {
  companyName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  taxId: '',
  status: '',
  statusChangeReason: ''
};

const normalizeDiscountRecord = (raw) => {
  if (!raw) return null;
  const discountType = raw.discountType || raw.discount_type || 'percent';
  const discountValueRaw = raw.discountValue ?? raw.discount_value;

  return {
    id: raw.id,
    plan: raw.plan || '',
    discountType,
    discountValue: typeof discountValueRaw === 'number' ? discountValueRaw : Number(discountValueRaw || 0),
    durationInMonths: raw.durationInMonths ?? raw.duration_in_months ?? null,
    startAt: raw.startAt || raw.start_at || null,
    endAt: raw.endAt || raw.end_at || null,
    couponId: raw.couponId || raw.coupon_id || null,
    status: raw.status || 'active'
  };
};

const formatDateForDisplay = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatCurrency = (value) => {
  if (!Number.isFinite(value)) return '£0.00';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const ClientManagement = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedClients, setSelectedClients] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    proSubscriptions: 0,
    essentialSubscriptions: 0
  });

  // Edit form state
  const [editForm, setEditForm] = useState(initialEditFormState);

  const [twoFactorInfo, setTwoFactorInfo] = useState(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmationEmail, setResetConfirmationEmail] = useState('');
  const [resettingTwoFactor, setResettingTwoFactor] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    type: 'percent',
    value: '',
    endDate: ''
  });
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [removingDiscount, setRemovingDiscount] = useState(false);

  const normalizeStaffApiPath = (rawPath) => {
    const trimmed = typeof rawPath === 'string' ? rawPath.trim() : '';
    if (!trimmed) {
      return '/api/staff';
    }
    let withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    if (!withLeadingSlash.startsWith('/api')) {
      withLeadingSlash = `/api${withLeadingSlash}`;
    }
    return withLeadingSlash;
  };

  const buildAuthProxyPath = (staffPath) => {
    const withoutApiPrefix = staffPath.startsWith('/api')
      ? staffPath.slice(4)
      : staffPath;
    return `/api/auth${withoutApiPrefix.startsWith('/') ? withoutApiPrefix : `/${withoutApiPrefix}`}`;
  };

  const callStaffManagement = async ({ path, method = 'get', payload = {} }) => {
    const staffPath = normalizeStaffApiPath(path);
    const methodUpper = String(method || 'get').toUpperCase();
    const isReadOnlyRequest = methodUpper === 'GET';
    const isSecurityPath = staffPath.includes('/staff/clients/');
    const fallbackBase = isSecurityPath
      ? AUTH_LAMBDA_FUNCTION_URL
      : STAFF_MANAGEMENT_PRIMARY_URL;
    const resolvedStaffId = user?.id || user?.userId || null;
    const staffHeaders = resolvedStaffId
      ? {
          'staff-id': resolvedStaffId,
          'user-id': resolvedStaffId
        }
      : {};

    const executeDirect = async () => {
      return callStaffLambdaDirect({
        path: staffPath,
        method,
        payload,
        additionalHeaders: staffHeaders
      });
    };

    if (isSecurityPath) {
      return callAuthPostWithFallback(api, {
        apiPath: staffPath,
        fallbackPath: staffPath,
        method,
        payload,
        authMode: 'query',
        requireAuthToken: true,
        allowFallbackOnProduction: true,
        forceFallback: true,
        fallbackBaseUrlOverride: fallbackBase
      });
    }

    if (isReadOnlyRequest) {
      try {
        return await executeDirect();
      } catch (directError) {
        console.warn('[ClientManagement] direct GET failed, using fallback', directError);
        return callAuthPostWithFallback(api, {
          apiPath: staffPath,
          fallbackPath: staffPath,
          method,
          payload,
          authMode: 'query',
          requireAuthToken: true,
          allowFallbackOnProduction: true,
          forceFallback: true,
          fallbackBaseUrlOverride: fallbackBase
        });
      }
    }

    try {
      return await executeDirect();
    } catch (directError) {
      console.warn('[ClientManagement] direct write failed, using fallback proxy', directError);
    }

    const proxyPath = buildAuthProxyPath(staffPath);

    return callAuthPostWithFallback(api, {
      apiPath: proxyPath,
      fallbackPath: staffPath,
      method,
      payload,
      authMode: 'query',
      requireAuthToken: true,
      allowFallbackOnProduction: true,
      forceFallback: true,
      fallbackBaseUrlOverride: fallbackBase
    });
  };

  // Fetch real client data from API
  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/clients');
      
      if (response.data.success && response.data.data) {
        const clientsArray = response.data.data.clients || response.data.data;
        const clientData = clientsArray.map(client => {
          // Handle both snake_case (from DB) and camelCase (from transformed response)
          const firstName = client.first_name || client.firstName || '';
          const lastName = client.last_name || client.lastName || '';
          const companyName = client.company_name || client.companyName || 'N/A';
          const contactName = `${firstName} ${lastName}`.trim() || 'N/A';
          
          // Check if this is an additional user
          const isAdditionalUser = client.is_additional_user || client.role === 'additional_user' || client.parent_user_id;
          const subscriptionDisplay = client.subscription_display || (isAdditionalUser ? 'Additional User' : (client.subscription_plan || client.subscriptionPlan || 'free'));
          
          return {
            id: client.id,
            companyName: companyName,
            firstName: firstName,
            lastName: lastName,
            contactName: contactName,
            email: client.email,
            phone: client.phone && client.phone !== 'N/A' ? client.phone : 'N/A',
            address: client.address && client.address !== 'N/A' ? client.address : 'N/A',
            city: client.city || '',
            state: client.state || '',
            zipCode: client.zip_code || client.zipCode || '',
            country: client.country || '',
            vatNumber: client.tax_id || client.taxId || 'N/A',
            registrationNumber: 'N/A',
            status: client.account_status || client.accountStatus || 'active',
            subscriptionPlan: isAdditionalUser ? (client.parent_subscription_plan || client.subscription_plan || client.subscriptionPlan || 'pro') : (client.subscription_plan || client.subscriptionPlan || 'free'),
            subscriptionDisplay: subscriptionDisplay, // For display: "Additional User" or plan name
            subscriptionStatus: client.subscription_status || client.subscriptionStatus || 'active',
            subscriptionStartDate: client.subscription_start_date || client.subscriptionStartDate ? 
                                  new Date(client.subscription_start_date || client.subscriptionStartDate).toLocaleDateString() : 'N/A',
            isAdditionalUser: isAdditionalUser,
            parentEmail: client.parent_email || null,
            parentName: client.parent_name || null,
            totalInvoices: 0, // Will be calculated from actual invoice data
            createdAt: client.created_at || client.createdAt
          };
        });
        
        setClients(clientData);
        
        // Calculate stats
        const totalClients = clientData.length;
        const activeClients = clientData.filter(c => c.status === 'active').length;
        const proSubscriptions = clientData.filter(c => c.subscriptionPlan === 'pro').length;
        const essentialSubscriptions = clientData.filter(c => c.subscriptionPlan === 'essential').length;
        
        setStats({
          totalClients,
          activeClients,
          proSubscriptions,
          essentialSubscriptions
        });
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to fetch client data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTwoFactorInfo = async (clientId) => {
    if (!clientId) {
      setTwoFactorInfo(null);
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorError(null);

    try {
      const response = await callStaffManagement({
        path: `/staff/clients/${clientId}/security`,
        method: 'get',
        payload: {},
      });
      if (response?.success) {
        setTwoFactorInfo(response.data || null);
      } else {
        setTwoFactorInfo(null);
        setTwoFactorError(response?.error || 'Unable to load two-factor details');
      }
    } catch (error) {
      console.error('Error fetching two-factor info:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load two-factor details';
      setTwoFactorInfo(null);
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const fetchDiscountInfo = async (clientId) => {
    if (!clientId) {
      setDiscountInfo(null);
      return;
    }

    setDiscountLoading(true);
    setDiscountError(null);

    try {
      const response = await callStaffManagement({
        path: `/api/staff/users/${clientId}/discount`,
        method: 'get',
        payload: {},
      });

      if (response?.success && response.discount) {
        const normalized = normalizeDiscountRecord(response.discount);
        setDiscountInfo(normalized);
        setDiscountForm({
          type: normalized.discountType || 'percent',
          value: normalized.discountValue ? String(normalized.discountValue) : '',
          endDate: normalized.endAt ? normalized.endAt.slice(0, 10) : ''
        });
      } else {
        setDiscountInfo(null);
        setDiscountForm({
          type: 'percent',
          value: '',
          endDate: ''
        });
      }
    } catch (error) {
      console.error('Error fetching discount info:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load discount information.';
      setDiscountInfo(null);
      setDiscountError(message);
    } finally {
      setDiscountLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Handle edit client
  const handleEditClient = (client) => {
    setEditingClient(client);
    setTwoFactorInfo(null);
    setTwoFactorError(null);
    setShowResetConfirm(false);
    setResetConfirmationEmail('');
    setEditForm({
      companyName: client.companyName !== 'N/A' ? client.companyName : '',
      firstName: client.firstName || '',
      lastName: client.lastName || '',
      email: client.email,
      phone: client.phone !== 'N/A' ? client.phone : '',
      address: client.address !== 'N/A' ? client.address : '',
      city: client.city || '',
      state: client.state || '',
      zipCode: client.zipCode || '',
      country: client.country || '',
      taxId: client.vatNumber !== 'N/A' ? client.vatNumber : '',
      status: client.status,
      statusChangeReason: ''
    });
    setShowEditModal(true);
    fetchTwoFactorInfo(client.id);
    fetchDiscountInfo(client.id);
  };

  // Handle save edited client
  const handleSaveEdit = async () => {
    try {
      setBulkActionLoading(true);
      
      if (editingClient && editForm.status !== editingClient.status) {
        const nextStatus = editForm.status;
        const trimmedReason = (editForm.statusChangeReason || '').trim();

        if (nextStatus === 'suspended' && trimmedReason.length === 0) {
          alert('Please provide a reason for suspending this account.');
          setBulkActionLoading(false);
          return;
        }
      }

      // Update user details
      const userUpdateData = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        companyName: editForm.companyName,
        phone: editForm.phone,
        address: editForm.address,
        city: editForm.city,
        state: editForm.state,
        zipCode: editForm.zipCode,
        country: editForm.country,
        taxId: editForm.taxId
      };

      // Update user
      const updateResponse = await callStaffManagement({
        path: `/api/staff/users/${editingClient.id}`,
        method: 'put',
        payload: userUpdateData,
      });

      if (updateResponse?.success === false) {
        throw new Error(updateResponse?.message || updateResponse?.error || 'Failed to update client details');
      }

      // Update account status if it changed
      if (editForm.status !== editingClient.status) {
        const payload = {
          status: editForm.status
        };

        if (editForm.status === 'suspended') {
          payload.reason = (editForm.statusChangeReason || '').trim();
        }

        const statusResponse = await callStaffManagement({
          path: `/api/staff/users/${editingClient.id}/status`,
          method: 'put',
          payload,
        });

        if (statusResponse?.success === false) {
          throw new Error(statusResponse?.message || statusResponse?.error || 'Failed to update account status');
        }
      }

      // Refresh client data
      await fetchClients();
      
      // Close modal and reset
      closeEditModal();
      
      // Show success message
      alert('Client updated successfully!');
      
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!editingClient) {
      alert('Select a client first.');
      return;
    }

    const plan = String(editingClient.subscriptionPlan || '').toLowerCase();
    if (!plan) {
      alert('Unable to determine the client’s current plan.');
      return;
    }

    const numericValue = Number(discountForm.value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      alert('Enter a discount value greater than zero.');
      return;
    }

    if (discountForm.type === 'percent' && (numericValue <= 0 || numericValue > 100)) {
      alert('Percentage discounts must be between 0 and 100.');
      return;
    }

    setSavingDiscount(true);
    setDiscountError(null);

    try {
      const response = await callStaffManagement({
        path: `/api/staff/users/${editingClient.id}/discount`,
        method: 'put',
        payload: {
          plan,
          discountType: discountForm.type,
          value: numericValue,
          endDate: discountForm.endDate || null
        }
      });

      if (response?.success) {
        alert('Discount applied successfully.');
        await fetchDiscountInfo(editingClient.id);
      } else {
        const message = response?.message || response?.error || 'Failed to apply discount.';
        setDiscountError(message);
        alert(message);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to apply discount.';
      setDiscountError(message);
      alert(message);
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!editingClient || !discountInfo) {
      return;
    }

    if (!window.confirm('Remove the active discount from this subscription?')) {
      return;
    }

    setRemovingDiscount(true);
    setDiscountError(null);

    try {
      const response = await callStaffManagement({
        path: `/api/staff/users/${editingClient.id}/discount`,
        method: 'delete',
        payload: {}
      });

      if (response?.success) {
        alert('Discount removed.');
        await fetchDiscountInfo(editingClient.id);
      } else {
        const message = response?.message || response?.error || 'Failed to remove discount.';
        setDiscountError(message);
        alert(message);
      }
    } catch (error) {
      console.error('Error removing discount:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove discount.';
      setDiscountError(message);
      alert(message);
    } finally {
      setRemovingDiscount(false);
    }
  };

  const handleConfirmTwoFactorReset = async () => {
    if (!editingClient) return;

    const expectedEmail = (editingClient.email || '').trim().toLowerCase();
    const providedEmail = resetConfirmationEmail.trim().toLowerCase();

    if (!expectedEmail || providedEmail !== expectedEmail) {
      setTwoFactorError('Email confirmation does not match the selected client.');
      return;
    }

    try {
      setResettingTwoFactor(true);
      setTwoFactorError(null);
      await callStaffManagement({
        path: `/staff/clients/${editingClient.id}/two-factor/reset`,
        method: 'post',
        payload: {},
      });
      alert('Two-factor authentication has been reset. The client will need to reconfigure at their next login.');
      setShowResetConfirm(false);
      setResetConfirmationEmail('');
      await fetchTwoFactorInfo(editingClient.id);
    } catch (error) {
      console.error('Error resetting two-factor authentication:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to reset two-factor authentication. Please try again.';
      setTwoFactorError(message);
      alert(message);
    } finally {
      setResettingTwoFactor(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingClient(null);
    setEditForm(initialEditFormState);
    setTwoFactorInfo(null);
    setTwoFactorError(null);
    setShowResetConfirm(false);
    setResetConfirmationEmail('');
    setTwoFactorLoading(false);
    setResettingTwoFactor(false);
    setDiscountInfo(null);
    setDiscountError(null);
    setDiscountLoading(false);
    setDiscountForm({
      type: 'percent',
      value: '',
      endDate: ''
    });
  };

  const getTwoFactorStatusDetails = () => {
    if (!twoFactorInfo) {
      return {
        label: 'Unknown',
        badgeClass: 'bg-gray-100 text-gray-600'
      };
    }

    switch (twoFactorInfo.status) {
      case 'enabled':
        return { label: 'Enabled', badgeClass: 'bg-green-100 text-green-700' };
      case 'reconfiguration_required':
        return { label: 'Reconfiguration required', badgeClass: 'bg-amber-100 text-amber-700' };
      default:
        return { label: 'Not configured', badgeClass: 'bg-gray-100 text-gray-600' };
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedClients.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      
      // Update all selected clients
      const updatePromises = selectedClients.map(async (clientId) => {
        const client = clients.find(c => c.id === clientId);
        if (!client) return;
        
        const newStatus = action === 'activate' ? 'active' : 'inactive';
        
        // Update user status
        await api.put(`/api/clients/${clientId}`, { status: newStatus });
        
        // Update subscription status if deactivating
        if (action === 'deactivate') {
          await callStaffManagement({
            path: `/api/staff/users/${clientId}/subscription`,
            method: 'put',
            payload: { status: 'suspended' }
          });
        } else if (action === 'activate' && client.subscriptionStatus === 'suspended') {
          await callStaffManagement({
            path: `/api/staff/users/${clientId}/subscription`,
            method: 'put',
            payload: { status: 'active' }
          });
        }
      });
      
      await Promise.all(updatePromises);
      
      // Refresh client data
      await fetchClients();
      
      // Clear selection
      setSelectedClients([]);
      
      // Show success message
      alert(`${action === 'activate' ? 'Activated' : 'Deactivated'} ${selectedClients.length} client(s) successfully!`);
      
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert(`Failed to ${action} clients. Please try again.`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Handle delete client
  const handleDeleteClient = async (client) => {
    if (!confirm(`Are you sure you want to delete ${client.companyName || client.contactName}? This action cannot be undone and will remove all their data.`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      
      // Delete user from backend
      await api.delete(`/api/clients/${client.id}`);
      
      // Refresh client data
      await fetchClients();
      
      // Show success message
      alert(`Client ${client.companyName || client.contactName} deleted successfully!`);
      
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Export selected clients to CSV
  const exportToCSV = () => {
    if (selectedClients.length === 0) {
      alert('Please select clients to export');
      return;
    }

    const selectedClientData = clients.filter(client => selectedClients.includes(client.id));
    
    // CSV headers
    const headers = [
      'Company Name',
      'Contact Name',
      'Email',
      'Phone',
      'Address',
      'City',
      'State',
      'Zip Code',
      'Country',
      'Tax ID',
      'Subscription Plan',
      'Subscription Status',
      'Status',
      'Created Date'
    ];

    // CSV data rows
    const csvData = selectedClientData.map(client => [
      client.companyName !== 'N/A' ? client.companyName : '',
      client.contactName !== 'N/A' ? client.contactName : '',
      client.email,
      client.phone !== 'N/A' ? client.phone : '',
      client.address !== 'N/A' ? client.address : '',
      client.city || '',
      client.state || '',
      client.zipCode || '',
      client.country || '',
      client.vatNumber !== 'N/A' ? client.vatNumber : '',
      client.subscriptionPlan,
      client.subscriptionStatus,
      client.status,
      client.createdAt ? new Date(client.createdAt).toLocaleDateString() : ''
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `clients_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
      suspended: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getSubscriptionBadge = (plan) => {
    if (!plan) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
    }
    
    // Handle "Additional User" display
    if (plan.toLowerCase() === 'additional user') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
          Additional User
        </span>
      );
    }
    
    const planConfig = {
      essential: { color: 'bg-blue-100 text-blue-800' },
      pro: { color: 'bg-purple-100 text-purple-800' },
      free_trial: { color: 'bg-orange-100 text-orange-800' },
      free: { color: 'bg-gray-100 text-gray-800' },
      trial: { color: 'bg-orange-100 text-orange-800' }
    };
    
    const planLower = plan.toLowerCase();
    const config = planConfig[planLower] || planConfig.essential;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {plan.charAt(0).toUpperCase() + plan.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">{error}</div>
          <button
            onClick={fetchClients}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Client Management</h1>
          <p className="text-gray-600 mt-1">Manage all client accounts and relationships</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchClients}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>

        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Clients</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalClients}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Clients</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeClients}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Building className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pro Subscriptions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.proSubscriptions}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Essential Subscriptions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.essentialSubscriptions}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search clients by name, company, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedClients.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedClients.length} client(s) selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction('activate')}
                disabled={bulkActionLoading}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkActionLoading ? 'Processing...' : 'Activate'}
              </button>
              <button
                onClick={() => handleBulkAction('deactivate')}
                disabled={bulkActionLoading}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkActionLoading ? 'Processing...' : 'Deactivate'}
              </button>
              <button
                onClick={exportToCSV}
                disabled={bulkActionLoading}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-3 h-3 inline mr-1" />
                Export CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clients Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedClients(clients.map(c => c.id));
                      } else {
                        setSelectedClients([]);
                      }
                    }}
                    checked={selectedClients.length === clients.length && clients.length > 0}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription Start
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    {clients.length === 0 ? 'No clients found' : 'No clients match your search criteria'}
                  </td>
                </tr>
              ) : (
                filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedClients([...selectedClients, client.id]);
                          } else {
                            setSelectedClients(selectedClients.filter(id => id !== client.id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{client.companyName}</div>
                        {client.vatNumber && client.vatNumber !== 'N/A' && (
                          <div className="text-sm text-gray-500">{client.vatNumber}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">{client.contactName}</div>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <Mail className="w-3 h-3" />
                          <span>{client.email}</span>
                        </div>
                        {client.phone !== 'N/A' && (
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <Phone className="w-3 h-3" />
                            <span>{client.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(client.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSubscriptionBadge(client.subscriptionDisplay || client.subscriptionPlan)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                              {client.subscriptionStartDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditClient(client)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Client"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredClients.length}</span> of{' '}
            <span className="font-medium">{clients.length}</span> results
          </div>
          <div className="flex items-center space-x-2">
            <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
              Previous
            </button>
            <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Edit Client Modal */}
      {showEditModal && editingClient && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Client</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              {/* Company Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={editForm.companyName}
                    onChange={(e) => setEditForm({...editForm, companyName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID / VAT Number</label>
                  <input
                    type="text"
                    value={editForm.taxId}
                    onChange={(e) => setEditForm({...editForm, taxId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Address Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={editForm.state}
                    onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                  <input
                    type="text"
                    value={editForm.zipCode}
                    onChange={(e) => setEditForm({...editForm, zipCode: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  value={editForm.country}
                  onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Status</label>
                {editingClient?.status === 'inactive' ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    This account is inactive because the user cancelled their subscription. Ask the customer to resubscribe from the client portal to regain access.
                  </div>
                ) : (
                  <select
                    value={editForm.status}
                    onChange={(e) => {
                      const nextStatus = e.target.value;
                      setEditForm((prev) => ({
                        ...prev,
                        status: nextStatus,
                        statusChangeReason: nextStatus === 'suspended' ? prev.statusChangeReason : ''
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                )}
              </div>

              {editForm.status === 'suspended' && editingClient?.status !== 'inactive' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Suspension reason</label>
                  <textarea
                    value={editForm.statusChangeReason}
                    onChange={(e) => setEditForm({...editForm, statusChangeReason: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Explain why this account is being suspended. The customer will receive this reason via email."
                  />
                </div>
              )}
            </div>

          {/* Subscription discount management */}
          <div className="border border-indigo-100 rounded-lg bg-indigo-50/40 p-4 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                  <Percent className="w-4 h-4 mr-2 text-indigo-500" />
                  Subscription discount
                </h4>
                <p className="text-xs text-gray-600 mt-1">
                  Discounts apply from the client’s next invoice. Only one discount can be active at a time.
                </p>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                {String(editingClient?.subscriptionPlan || 'unknown').toUpperCase()}
              </span>
            </div>

            {discountLoading ? (
              <div className="flex items-center text-sm text-gray-500">
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Loading discount details...
              </div>
            ) : discountError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start">
                <AlertCircleIcon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Unable to load discount info</p>
                  <p>{discountError}</p>
                </div>
              </div>
            ) : discountInfo ? (
              <div className="rounded-md border border-indigo-200 bg-white p-3 text-sm text-gray-700">
                <p className="font-semibold text-gray-900">Current discount</p>
                <ul className="mt-1 space-y-1">
                  <li>
                    Type:{' '}
                    {discountInfo.discountType === 'percent'
                      ? `${discountInfo.discountValue}% off`
                      : `${formatCurrency(discountInfo.discountValue)} off`}
                  </li>
                  <li>Starts: {formatDateForDisplay(discountInfo.startAt) || 'Next invoice'}</li>
                  <li>
                    Ends:{' '}
                    {discountInfo.endAt
                      ? formatDateForDisplay(discountInfo.endAt)
                      : discountInfo.durationInMonths
                        ? `${discountInfo.durationInMonths} billing cycle${discountInfo.durationInMonths > 1 ? 's' : ''}`
                        : 'Until removed'}
                  </li>
                  {discountInfo.couponId && (
                    <li className="text-xs text-gray-500">Stripe coupon: {discountInfo.couponId}</li>
                  )}
                </ul>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-indigo-200 bg-white p-3 text-sm text-gray-600">
                No active discount. Use the form below to add one.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Discount type</label>
                <select
                  value={discountForm.type}
                  onChange={(e) => setDiscountForm((prev) => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                >
                  <option value="percent">Percentage</option>
                  <option value="amount">Fixed amount (£)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {discountForm.type === 'percent' ? 'Percent off (0-100)' : 'Amount off (£)'}
                </label>
                <input
                  type="number"
                  min="0"
                  max={discountForm.type === 'percent' ? '100' : undefined}
                  step={discountForm.type === 'percent' ? '1' : '0.01'}
                  value={discountForm.value}
                  onChange={(e) => setDiscountForm((prev) => ({ ...prev, value: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  placeholder={discountForm.type === 'percent' ? 'e.g. 10' : 'e.g. 15.00'}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Discount end date (optional)</label>
                <input
                  type="date"
                  value={discountForm.endDate}
                  onChange={(e) => setDiscountForm((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Leave blank for an indefinite discount. When set, the offer ends after that billing cycle.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleApplyDiscount}
                disabled={savingDiscount || removingDiscount || bulkActionLoading}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingDiscount ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save discount'
                )}
              </button>
              {discountInfo && (
                <button
                  onClick={handleRemoveDiscount}
                  disabled={removingDiscount || savingDiscount || bulkActionLoading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {removingDiscount ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    'Remove discount'
                  )}
                </button>
              )}
            </div>
          </div>

              {/* Security & Two-Factor */}
              <div className="border border-gray-200 rounded-lg bg-gray-50 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-blue-500" />
                      Security &amp; Two-Factor Authentication
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Review the client&rsquo;s current two-factor status or reset their authenticator if they&rsquo;re locked out.
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTwoFactorStatusDetails().badgeClass}`}
                  >
                    {getTwoFactorStatusDetails().label}
                  </span>
                </div>

                <div className="mt-4 space-y-3">
                  {twoFactorLoading ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading security details...
                    </div>
                  ) : twoFactorError ? (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start">
                      <AlertCircleIcon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Unable to load two-factor data</p>
                        <p>{twoFactorError}</p>
                      </div>
                    </div>
                  ) : twoFactorInfo ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
                        <div>
                          <span className="block text-xs uppercase text-gray-500 tracking-wide">First verified</span>
                          <span>{twoFactorInfo.firstVerifiedAt ? new Date(twoFactorInfo.firstVerifiedAt).toLocaleString() : 'Not yet verified'}</span>
                        </div>
                        <div>
                          <span className="block text-xs uppercase text-gray-500 tracking-wide">Last verified</span>
                          <span>{twoFactorInfo.verifiedAt ? new Date(twoFactorInfo.verifiedAt).toLocaleString() : 'Not recorded'}</span>
                        </div>
                        <div>
                          <span className="block text-xs uppercase text-gray-500 tracking-wide">Grace period ends</span>
                          <span>{twoFactorInfo.gracePeriodEndsAt ? new Date(twoFactorInfo.gracePeriodEndsAt).toLocaleDateString() : 'Not applicable'}</span>
                        </div>
                        <div>
                          <span className="block text-xs uppercase text-gray-500 tracking-wide">Days remaining</span>
                          <span>{typeof twoFactorInfo.daysRemaining === 'number' ? twoFactorInfo.daysRemaining : 'N/A'}</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setShowResetConfirm(true);
                          setTwoFactorError(null);
                          setResetConfirmationEmail('');
                        }}
                        className="mt-3 inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        Reset Two-Factor Authentication
                      </button>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Two-factor details are not available for this client.
                    </div>
                  )}

                  {showResetConfirm && (
                    <div className="border border-red-200 bg-red-50 rounded-md p-3">
                      <p className="text-sm font-medium text-red-700">
                        Confirm reset for {editingClient.email}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        This will invalidate their existing authenticator. They must complete setup again at their next login.
                      </p>
                      <label className="block text-xs text-gray-700 mt-3 mb-1">
                        Type the client&rsquo;s email to confirm
                      </label>
                      <input
                        type="email"
                        value={resetConfirmationEmail}
                        onChange={(e) => setResetConfirmationEmail(e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/50"
                        placeholder={editingClient.email}
                      />
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleConfirmTwoFactorReset}
                          disabled={
                            resettingTwoFactor ||
                            resetConfirmationEmail.trim().toLowerCase() !== (editingClient.email || '').trim().toLowerCase()
                          }
                          className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            resettingTwoFactor ||
                            resetConfirmationEmail.trim().toLowerCase() !== (editingClient.email || '').trim().toLowerCase()
                              ? 'bg-red-300 cursor-not-allowed'
                              : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                          }`}
                        >
                          {resettingTwoFactor ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Resetting...
                            </>
                          ) : (
                            'Confirm Reset'
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowResetConfirm(false);
                            setResetConfirmationEmail('');
                            setTwoFactorError(null);
                          }}
                          className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkActionLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 inline mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 inline mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;
